// Compact event cards used in the EventsPage Open / In Progress / Past tabs.
// Keeps the visual rhythm consistent across SessionRow + LadderRow +
// TournamentRow so a member doesn't perceive them as three different
// modules slammed together.
import React from 'react';
import { ListNumbers, Trophy, Sparkle, Users } from '@phosphor-icons/react';
import ManageEventPill from './ManageEventPill';

const CATEGORY_LABEL = {
  social:  'Social',
  tennis:  'Tennis',
  charity: 'Charity',
  club:    'Club',
};

function Tone({ children, tone }) {
  const cls = {
    green: 'bg-[#015e3f]/10 text-[#015e3f]',
    amber: 'bg-amber-50 text-amber-800 border border-amber-200',
    gold:  'bg-[#fffbf0] text-[#c9a93a] border border-[#c9a93a]/50',
    gray:  'bg-[#e3dcd1]/60 text-[#6a766e]',
  }[tone];
  return (
    <span className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${cls}`}>{children}</span>
  );
}

export function LadderRow({ ladder, onClick }) {
  // ladder = { slug, name, gender, route, status }
  const isClosed = ladder.status === 'closed';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`ladder-row-${ladder.slug}`}
      className="w-full text-left bg-white border border-[#e3dcd1] rounded-2xl p-4 space-y-2 hover:border-[#015e3f]/40 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex items-center gap-3">
          <div className="shrink-0 w-9 h-9 rounded-xl bg-[#015e3f]/10 text-[#015e3f] flex items-center justify-center">
            <ListNumbers size={18} weight="fill" />
          </div>
          <div className="min-w-0">
            <div className="text-sm text-[#013523] font-medium truncate">{ladder.name}</div>
            <div className="text-[11px] text-[#6a766e]">
              {ladder.gender === 'mens' ? "Men's" : ladder.gender === 'womens' ? 'Women\u2019s' : 'Mixed'} singles · challenge-based
            </div>
          </div>
        </div>
        {isClosed
          ? <Tone tone="amber">Closed to new players</Tone>
          : <Tone tone="green">Always Open</Tone>}
      </div>
    </button>
  );
}

export function TournamentRow({ tournament, onClick }) {
  const t = tournament;
  const isOpen = ['draft', 'registration_open', 'registration_closed'].includes(t.status);
  const isLive = ['bracket_generated', 'in_progress'].includes(t.status);
  const isDone = t.status === 'completed';
  let badge;
  if (isOpen)      badge = { text: 'Registration open', tone: 'gold' };
  else if (isLive) badge = { text: 'In progress',        tone: 'green' };
  else if (isDone) badge = { text: 'Completed',          tone: 'gray' };
  else             badge = { text: (t.status || '').replace(/_/g, ' '), tone: 'amber' };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`tournament-row-${t.id}`}
      className="w-full text-left bg-white border border-[#e3dcd1] rounded-2xl p-4 space-y-2 hover:border-[#c9a93a]/60 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex items-center gap-3">
          <div className="shrink-0 w-9 h-9 rounded-xl bg-[#fffbf0] text-[#c9a93a] flex items-center justify-center">
            <Trophy size={18} weight="fill" />
          </div>
          <div className="min-w-0">
            <div className="text-sm text-[#013523] font-medium truncate">{t.name}</div>
            <div className="text-[11px] text-[#6a766e] flex items-center gap-1.5 flex-wrap">
              <span>
                {t.tournament_type === 'singles' ? 'Singles' : 'Doubles'} ·
                {' '}{t.entry_count} {t.entry_count === 1 ? 'entry' : 'entries'}
                {t.bracket_size ? ` · ${t.bracket_size}-slot bracket` : ''}
              </span>
              {t.is_alternate && (
                <span
                  data-testid={`tournament-row-alt-${t.id}`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5"
                >
                  Alternate
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <ManageEventPill eventKind="tournament" eventId={t.id} variant="card" onClick={(e) => e.stopPropagation()} />
          <Tone tone={badge.tone}>{badge.text}</Tone>
        </div>
      </div>
    </button>
  );
}

export function RoundRobinRow({ rr, onClick }) {
  const isOpen = ['draft', 'registration_open', 'registration_closed'].includes(rr.status);
  const isLive = ['schedule_generated', 'in_progress'].includes(rr.status);
  const isDone = rr.status === 'completed';
  let badge;
  if (isOpen)      badge = { text: 'Registration open', tone: 'gold' };
  else if (isLive) badge = { text: 'In progress',        tone: 'green' };
  else if (isDone) badge = { text: 'Completed',          tone: 'gray' };
  else             badge = { text: (rr.status || '').replace(/_/g, ' '), tone: 'amber' };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`round-robin-row-${rr.id}`}
      className="w-full text-left bg-white border border-[#e3dcd1] rounded-2xl p-4 space-y-2 hover:border-[#015e3f]/40 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex items-center gap-3">
          <div className="shrink-0 w-9 h-9 rounded-xl bg-[#015e3f]/10 text-[#015e3f] flex items-center justify-center">
            <Trophy size={18} weight="duotone" />
          </div>
          <div className="min-w-0">
            <div className="text-sm text-[#013523] font-medium truncate">{rr.name}</div>
            <div className="text-[11px] text-[#6a766e] flex items-center gap-1.5 flex-wrap">
              <span>
                Round Robin · {rr.rr_type === 'singles' ? 'Singles' : 'Doubles'} ·
                {' '}{rr.num_pools} pool{rr.num_pools > 1 ? 's' : ''} ·
                {' '}{rr.entry_count || 0} {rr.entry_count === 1 ? 'entry' : 'entries'}
              </span>
              {rr.is_alternate && (
                <span
                  data-testid={`round-robin-row-alt-${rr.id}`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5"
                >
                  Alternate
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <ManageEventPill eventKind="round_robin" eventId={rr.id} variant="card" onClick={(e) => e.stopPropagation()} />
          <Tone tone={badge.tone}>{badge.text}</Tone>
        </div>
      </div>
    </button>
  );
}


// -- ClubEventRow -----------------------------------------------------------
// Time-based social/announcement events. No registration, no roster. Placement
// is derived from `starts_at`/`ends_at` (see EventsPage bucketing), so this
// row only needs to render the badge the parent tab implies.
export function ClubEventRow({ event, phase, onClick }) {
  // phase: 'upcoming' | 'in_progress' | 'past'
  let badge;
  if (phase === 'in_progress')   badge = { text: 'Happening now', tone: 'green' };
  else if (phase === 'past')     badge = { text: 'Past',          tone: 'gray' };
  else                           badge = { text: 'Upcoming',      tone: 'gold' };

  // Compact date/time hint so the row is self-explanatory without opening
  // the detail page. Falls back gracefully if dates parse oddly.
  let when = '';
  try {
    const d = new Date(event.starts_at);
    if (!Number.isNaN(d.getTime())) {
      when = d.toLocaleDateString(undefined, {
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    }
  } catch { /* ignore */ }

  const categoryLabel = event.category ? CATEGORY_LABEL[event.category] : null;
  const showGoing = event.rsvp_enabled && (event.attendee_count || 0) > 0;
  // Small price indicator only when a positive amount is configured. 0 or
  // null → no indicator (0 alone is not "payment enabled").
  const feeCents = event.entry_fee_cents;
  const feeText = Number.isFinite(feeCents) && feeCents > 0
    ? (feeCents % 100 === 0 ? `$${feeCents / 100}` : `$${(feeCents / 100).toFixed(2)}`)
    : null;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`club-event-row-${event.id}`}
      className="w-full text-left bg-white border border-[#e3dcd1] rounded-2xl p-4 space-y-2 hover:border-[#c9a93a]/60 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex items-center gap-3">
          <div className="shrink-0 w-9 h-9 rounded-xl bg-[#f4efe6] text-[#7c5b1a] flex items-center justify-center">
            <Sparkle size={18} weight="fill" />
          </div>
          <div className="min-w-0">
            <div className="text-sm text-[#013523] font-medium truncate flex items-center gap-1.5 flex-wrap">
              <span className="truncate">{event.name}</span>
              {categoryLabel && (
                <span
                  data-testid={`club-event-row-category-${event.id}`}
                  className="text-[9.5px] uppercase tracking-wider bg-[#f0e6cf] text-[#7c5b1a] border border-[#c9a93a]/40 rounded-full px-1.5 py-[1px] font-semibold"
                >
                  {categoryLabel}
                </span>
              )}
            </div>
            <div className="text-[11px] text-[#6a766e] flex items-center gap-1.5 flex-wrap">
              <span>Club Event{when ? ` · ${when}` : ''}{event.location_text ? ` · ${event.location_text}` : ''}</span>
              {feeText && (
                <span
                  data-testid={`club-event-row-fee-${event.id}`}
                  className="inline-flex items-center gap-0.5 text-[11px] text-[#7c5b1a] font-medium"
                >
                  · {feeText}
                </span>
              )}
              {showGoing && (
                <span
                  data-testid={`club-event-row-going-${event.id}`}
                  className="inline-flex items-center gap-0.5 text-[11px] text-[#015e3f] font-medium"
                >
                  <Users size={11} weight="bold" /> {event.attendee_count} registered
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <ManageEventPill eventKind="club_event" eventId={event.id} variant="card" onClick={(e) => e.stopPropagation()} />
          <Tone tone={badge.tone}>{badge.text}</Tone>
        </div>
      </div>
    </button>
  );
}
